import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://idobridalmagazinemalta.com';
const DEFAULT_IMAGE = `${SITE_URL}/social-sharing-banner.png`;
const SITE_NAME = 'I DO Bridal Magazine Malta';
const PUBLISHER = 'Standard Publications Ltd';

/**
 * Per-page SEO meta tags + JSON-LD structured data.
 *
 * Sets <title>, description, canonical URL, OG / Twitter sharing tags, and
 * schema.org JSON-LD (WebSite + Organization on every page; Article when
 * `type="article"`).
 *
 * Usage:
 *   <SEO
 *     title="Emma & David"
 *     description="A sun-drenched Maltese celebration..."
 *     image="https://..."
 *     type="article"
 *     keywords="malta wedding, palazzo parisio"   // optional
 *     publishedDate="2025-06-15"                  // optional, ISO
 *     author="I DO Editorial Team"                // optional
 *     breadcrumbs={[{ name: 'Real Weddings', url: '/wedding-stories' }]}
 *   />
 */
const SEO = ({
  title,
  description,
  image,
  type = 'website',
  keywords,
  publishedDate,
  modifiedDate,
  author,
  breadcrumbs,
}) => {
  const { pathname } = useLocation();
  const url = `${SITE_URL}${pathname}`;
  const fullTitle = title
    ? `${title} | ${SITE_NAME}`
    : `${SITE_NAME} | Where "Yes" Turns Into "I Do"`;
  const ogImage = image || DEFAULT_IMAGE;
  const desc =
    description ||
    "Malta's premier bridal magazine featuring real wedding stories, expert planning tips, top wedding vendors, and inspiration for your dream Mediterranean wedding.";

  // --- JSON-LD structured data ---
  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.svg`,
    description: 'Malta\'s premier bridal magazine',
    publisher: { '@type': 'Organization', name: PUBLISHER },
    sameAs: [
      'https://www.instagram.com/idomagofficial/',
      'https://www.facebook.com/idobridalmagazinemalta/',
    ],
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    description: desc,
    inLanguage: 'en-MT',
    publisher: { '@type': 'Organization', name: PUBLISHER },
  };

  const articleSchema =
    type === 'article'
      ? {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: title || fullTitle,
          description: desc,
          image: [ogImage],
          datePublished: publishedDate || undefined,
          dateModified: modifiedDate || publishedDate || undefined,
          author: {
            '@type': author ? 'Person' : 'Organization',
            name: author || SITE_NAME,
          },
          publisher: {
            '@type': 'Organization',
            name: SITE_NAME,
            logo: { '@type': 'ImageObject', url: `${SITE_URL}/favicon.svg` },
          },
          mainEntityOfPage: { '@type': 'WebPage', '@id': url },
          keywords: keywords || undefined,
        }
      : null;

  const breadcrumbSchema =
    breadcrumbs && breadcrumbs.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
            ...breadcrumbs.map((b, i) => ({
              '@type': 'ListItem',
              position: i + 2,
              name: b.name,
              item: b.url.startsWith('http') ? b.url : `${SITE_URL}${b.url}`,
            })),
          ],
        }
      : null;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_MT" />
      {publishedDate && <meta property="article:published_time" content={publishedDate} />}
      {modifiedDate && <meta property="article:modified_time" content={modifiedDate} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD structured data for rich Google results */}
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      {articleSchema && (
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      )}
      {breadcrumbSchema && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      )}
    </Helmet>
  );
};

export default SEO;
